import { Tooltip } from "@jugl-web/ui-kit";
import { assignRefs, cx, useAppVariant } from "@jugl-web/utils";
import {
  ButtonHTMLAttributes,
  forwardRef,
  HTMLAttributes,
  ReactNode,
} from "react";

export interface RoundedButtonProps
  extends Omit<HTMLAttributes<HTMLButtonElement>, "disabled" | "title"> {
  size: "sm" | "md";
  color: string;
  icon: ReactNode;
  hint: string;
  /**
   * Whether to use a native `title` (for performance reasons)
   * or a `Tooltip` component to display the hint
   */
  hintType?: "title" | "tooltip";
  isDisabled?: boolean;
}

export const RoundedButton = forwardRef<HTMLButtonElement, RoundedButtonProps>(
  (
    {
      size,
      color,
      icon,
      hint,
      hintType = "tooltip",
      isDisabled,
      className,
      ...props
    },
    ref
  ) => {
    const { isMobile } = useAppVariant();

    const commonButtonProps: ButtonHTMLAttributes<HTMLButtonElement> = {
      ...props,
      children: icon,
      className: cx(
        "flex items-center justify-center rounded-full border-0 transition",
        isDisabled
          ? "opacity-50"
          : "cursor-pointer hover:brightness-90 active:scale-95",
        {
          "h-7 w-7": size === "sm",
          "h-10 w-10": size === "md",
        },
        className
      ),
      style: { backgroundColor: color },
      disabled: isDisabled,
    };

    if (hintType === "tooltip" && !isMobile) {
      return (
        <Tooltip
          placement="bottom"
          renderTrigger={({ ref: triggerRef, props: triggerProps }) => (
            <button
              ref={assignRefs([ref, triggerRef])}
              type="button"
              {...commonButtonProps}
              {...triggerProps}
            />
          )}
        >
          {hint}
        </Tooltip>
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        title={!isDisabled ? hint : undefined}
        {...commonButtonProps}
      />
    );
  }
);
